import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyConfig, NgxCurrencyDirective } from 'ngx-currency';
import { Subscription } from 'rxjs';

import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { MotorFiscalLookupService } from '../../../../../shared/services/apis/motor-fiscal/motor-fiscal-lookup.service';
import { MaskService } from '../../../../../shared/services/mask.service';
import { UtilService } from '../../../../../shared/services/util.service';
import { LookupItemStruct } from '../../../../../shared/services/structs/motor-fiscal/lookup-item.struct';
import { FormaEnvioSelectStruct } from '../../../../../shared/services/structs/vendas/forma-envio-select.struct';
import { FormaFreteSelectStruct } from '../../../../../shared/services/structs/vendas/forma-frete-select.struct';
import { calcularTotaisDoDocumento } from '../vd-totais/venda-totais.calculo';

/** Codigo SEFAZ da modalidade "0 - Contratacao do Frete por conta do Remetente (CIF)", o padrao da §3.1. */
const CODIGO_MODALIDADE_CIF = '0';

/**
 * Secao Transporte (story §3.1), com as subsecoes Envio e frete, Transportador e Volumes (a mesma
 * organizacao da NF-e de saida). O titulo e o recolher da secao sao do shell.
 *
 * Forma de envio -> Forma de frete -> Transportador, em cascata: o frete e um SERVICO da forma
 * de envio (PAC, SEDEX, entrega expressa), e a transportadora e a PESSOA que o frete aponta. O
 * transportador aparece em LEITURA, resolvido pelo id (decisao D11: sem coluna de texto) — e e o
 * id que a NF-e quer, porque no XML o transportador e pessoa.
 *
 * No PEDIDO o Envio e frete tem mais: frete por conta (tabela fiscal modFrete), rastreio e o
 * envio para expedicao - esses dois so aparecem com a forma de envio definida, como no prototipo.
 *
 * Volumes: a tela edita UM volume (o grupo `volume` do form), como a NF-e de saida; o shell o
 * monta do primeiro volume gravado e o envia como lista no salvamento. Os pesos do volume nascem
 * sugeridos pela soma dos itens (peso x quantidade), como na NF-e de saida.
 *
 * As formas de envio vem do agregado de configuracoes (cacheadas por empresa); as modalidades de
 * frete vem do MotorFiscal, carregadas aqui — a API.MotorFiscal segue intocada, so e consumida.
 */
@Component({
    selector: 'app-vd-transporte',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatTooltipModule,
        MatSlideToggleModule,
        NgxCurrencyDirective,
        SelectPesquisavelComponent,
    ],
    templateUrl: './vd-transporte.component.html',
    styleUrl: './vd-transporte.component.css',
})
export class VdTransporteComponent implements OnInit, OnChanges, OnDestroy {
    @Input({ required: true }) public form!: FormGroup;
    @Input() public ehOrcamento: boolean = true;
    @Input() public isSomenteLeitura: boolean = false;

    /** Documento ainda nao gravado: e quando o "Frete por conta" nasce em CIF. */
    @Input() public ehNovo: boolean = true;

    /** Formas de envio ativas da empresa, com os fretes de cada uma (agregado). */
    @Input() public formasEnvio: FormaEnvioSelectStruct[] = [];

    /**
     * O nome do transportador GRAVADO no documento, resolvido pelo orquestrador. Vale enquanto o
     * usuario nao troca o frete — a partir dai o nome e o do frete escolhido.
     */
    @Input() public nomeTransportadorGravado: string | null = null;

    /** "Enviar para expedicao" so faz sentido quando a empresa trabalha com expedicao. */
    @Input() public exibeExpedicao: boolean = true;

    @Input({ required: true }) public mascaraInteira!: Partial<NgxCurrencyConfig>;

    public modalidadesFrete: LookupItemStruct[] = [];

    /** Peso do volume em kg, 3 casas, como o volume da NF-e de saida. */
    public readonly mascaraPeso: Partial<NgxCurrencyConfig>;

    /**
     * Os servicos de frete da forma escolhida — vazio sem forma, ou quando ela nao tem servicos.
     * CAMPO, e nao getter: vai em `[offlineList]`, e o `select-pesquisavel` reprocessa a cada
     * referencia nova. Recalculado quando a forma muda e quando o agregado chega.
     */
    public fretesDaForma: FormaFreteSelectStruct[] = [];

    /** O shell recria a seção a cada salvar e o form é dele: as assinaturas são canceladas com o componente. */
    private assinaturas: Subscription[] = [];

    private ultimoPesoBrutoAplicado: number | null = null;
    private ultimoPesoLiquidoAplicado: number | null = null;

    constructor(
        private motorFiscalLookupService: MotorFiscalLookupService,
        private maskService: MaskService,
    ) {
        this.mascaraPeso = { ...this.maskService.getDecimalMask(3), allowNegative: false };
    }

    public ngOnInit(): void {
        // A cascata: trocar a forma de envio invalida o frete (que e dela) e o transportador (que
        // e do frete). A URL de rastreamento da forma entra como sugestao quando o campo esta vazio.
        this.atualizarFretesDaForma();

        this.assinaturas.push(this.formaEnvioControl.valueChanges.subscribe(() => {
            this.form.patchValue({ idFormaFrete: null, idPessoaTransportador: null }, { emitEvent: false });
            this.atualizarFretesDaForma();

            const forma = this.formaEnvioSelecionada;

            if (forma?.urlRastreamento && !this.form.get('urlRastreamento')?.value) {
                this.form.patchValue({ urlRastreamento: forma.urlRastreamento }, { emitEvent: false });
            }
        }));

        this.assinaturas.push(this.formaFreteControl.valueChanges.subscribe(() => {
            this.form.patchValue(
                { idPessoaTransportador: this.freteSelecionado?.idPessoaTransportador ?? null },
                { emitEvent: false },
            );
        }));

        if (!this.ehOrcamento) this.carregarModalidadesFrete();

        const itens = this.form.get('itens') as FormArray | null;

        if (itens && !this.ehOrcamento) {
            this.aplicarPesosSugeridos();
            this.assinaturas.push(itens.valueChanges.subscribe(() => this.aplicarPesosSugeridos()));
        }
    }

    /**
     * O peso sugerido entra enquanto o campo está vazio ou ainda com a última sugestão aplicada;
     * peso digitado (ou gravado diferente da soma) não é sobrescrito.
     */
    private aplicarPesosSugeridos(): void {
        const volume = this.volume;
        if (!volume || this.isSomenteLeitura) return;

        const totais = calcularTotaisDoDocumento(this.form.get('itens') as FormArray | null);

        this.ultimoPesoBrutoAplicado = this.aplicarSugestao(volume.get('pesoBruto'), totais.pesoBruto, this.ultimoPesoBrutoAplicado);
        this.ultimoPesoLiquidoAplicado = this.aplicarSugestao(volume.get('pesoLiquido'), totais.pesoLiquido, this.ultimoPesoLiquidoAplicado);
    }

    private aplicarSugestao(controle: AbstractControl | null, sugestao: number, ultimoAplicado: number | null): number | null {
        if (!controle || controle.dirty) return ultimoAplicado;

        const atual = Number(controle.value ?? 0) || 0;

        // Peso em kg com 3 casas, como o volume da NF-e de saída.
        const valor = sugestao > 0 ? UtilService.arredondarAbnt(Number(sugestao.toPrecision(15)), 3) : null;

        // Gravado igual à soma (documento reaberto): conta como sugestão e continua acompanhando os itens.
        if (ultimoAplicado == null && atual !== 0 && atual === valor) return valor;

        if (atual !== 0 && atual !== ultimoAplicado) return ultimoAplicado;

        controle.setValue(valor, { emitEvent: false });
        return valor;
    }

    private carregarModalidadesFrete(): void {
        this.motorFiscalLookupService.getModalidadesFrete().subscribe({
            next: (lista) => {
                this.modalidadesFrete = lista ?? [];

                // §3.1: o padrao e "0 - CIF". So no documento NOVO e so quando nada foi escolhido.
                if (this.ehNovo && this.form.get('idModalidadeFrete')?.value == null) {
                    const cif = this.modalidadesFrete.find((m) => m.codigo === CODIGO_MODALIDADE_CIF);

                    if (cif) this.form.patchValue({ idModalidadeFrete: cif.id }, { emitEvent: false });
                }
            },
            error: () => {
                this.modalidadesFrete = [];
            },
        });
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['formasEnvio'] && this.form) this.atualizarFretesDaForma();
    }

    public ngOnDestroy(): void {
        this.assinaturas.forEach((a) => a.unsubscribe());
    }

    private atualizarFretesDaForma(): void {
        this.fretesDaForma = this.formaEnvioSelecionada?.fretes ?? [];
    }

    public get volume(): FormGroup {
        return this.form.get('volume') as FormGroup;
    }

    public get formaEnvioControl(): FormControl {
        return this.form.get('idFormaEnvio') as FormControl;
    }

    public get formaFreteControl(): FormControl {
        return this.form.get('idFormaFrete') as FormControl;
    }

    public get modalidadeFreteControl(): FormControl {
        return this.form.get('idModalidadeFrete') as FormControl;
    }

    public get formaEnvioSelecionada(): FormaEnvioSelectStruct | null {
        const id = this.formaEnvioControl?.value;

        return id ? this.formasEnvio.find((f) => f.idFormaEnvio === id) ?? null : null;
    }

    public get freteSelecionado(): FormaFreteSelectStruct | null {
        const id = this.formaFreteControl?.value;

        return id ? this.fretesDaForma.find((f) => f.idFormaFrete === id) ?? null : null;
    }

    /** Em leitura: o do frete escolhido; sem frete, o que veio gravado no documento. */
    public get nomeTransportador(): string {
        if (this.freteSelecionado) return this.freteSelecionado.nomePessoaTransportador ?? '';

        return this.form.get('idPessoaTransportador')?.value ? (this.nomeTransportadorGravado ?? '') : '';
    }

    /** §3.1: rastreio e expedicao [PED] so aparecem com forma de envio definida. */
    public get temFormaEnvio(): boolean {
        return this.formaEnvioControl?.value != null;
    }
}
